import { useEffect, useState, type FormEvent } from 'react';
import { KeyRound, Server, User } from 'lucide-react';
import { ErrorMessage, SegmentedControl } from '@shared/ui';
import { BRAND_GRADIENT, gradientTextStyle } from '@shared/styles/brand';
import { useCqgConnect } from '@features/cqg/hooks/useCqgConnect';
import { useCqgDisplayState } from '@features/cqg/hooks/useCqgDisplayState';
import { readStoredCqgCreds, type CqgEnvironment } from '@features/cqg/lib/cqgCreds';
import { runCqgConnectSequence } from '@features/cqg/lib/cqgConnectFlow';
import { CqgLogonErrorNotice } from '@features/cqg/components/CqgLogonErrorNotice';
import { useRithmicConnect } from '@features/rithmic/hooks/useRithmicConnect';
import { useRithmicDisplayState } from '@features/rithmic/hooks/useRithmicDisplayState';
import { DEFAULT_RITHMIC_SYSTEM, readStoredRithmicCreds, RITHMIC_SYSTEMS } from '@features/rithmic/lib/rithmicCreds';
import { runRithmicConnectSequence } from '@features/rithmic/lib/rithmicConnectFlow';
import { RithmicAttribution } from '@features/rithmic/components/RithmicAttribution';
import { useConnectDialogStore } from '../state/connectDialogStore';
import { ConnectModalShell, PillInput, PillSelect } from './ConnectModalShell';

type Broker = 'cqg' | 'rithmic';

/**
 * The Figma "Add account" dialog (2026-07 redesign): broker picker (CQG | Rithmic) over the real
 * per-broker connect fields — CQG = Demo/Live + username + password, Rithmic = system + username +
 * password. (The Figma's placeholder fields — "Gateway", "Email", "CQG code", "Add trade" — were
 * design filler; gateways resolve server-side from the environment/system, decision 2026-07-17.)
 *
 * Submitting runs the SAME extracted connect sequence as the provider dialogs (auto-resume re-arm,
 * relay-first, creds stash, popup arm) — a bare mutate would skip those safety steps. Progress and
 * errors come from the shared display-state hooks; edge flows keep their machinery in the legacy
 * CQG dialog: a concurrent-session answer hands off to it (via connectDialogStore) for take-over.
 */
export function AddAccountDialog() {
  const store = useConnectDialogStore();
  const open = store.addAccountOpen;

  const cqgConnect = useCqgConnect();
  const rithmicConnect = useRithmicConnect();
  const cqgState = useCqgDisplayState();
  const rithmicState = useRithmicDisplayState();

  const [broker, setBroker] = useState<Broker>('cqg');
  const [environment, setEnvironment] = useState<CqgEnvironment>('live');
  const [system, setSystem] = useState<string>(DEFAULT_RITHMIC_SYSTEM);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  /** Which broker THIS dialog initiated a connect for — gates the live/handoff effects below. */
  const [submitted, setSubmitted] = useState<Broker | null>(null);

  // Seed per-broker prefills from storage on every open (fresh dialog, no stale mount state).
  useEffect(() => {
    if (!open) return;
    setSubmitted(null);
    if (broker === 'cqg') {
      const stored = readStoredCqgCreds();
      setUsername(stored.username);
      setPassword(stored.password);
      setEnvironment(stored.environment);
    } else {
      const stored = readStoredRithmicCreds();
      setUsername(stored.username);
      setPassword(stored.password);
      setSystem(stored.system || DEFAULT_RITHMIC_SYSTEM);
    }
  }, [open, broker]);

  // Success: the submitted provider went live in THIS tab → the account is linked; close.
  const submittedDisplay = submitted === 'cqg' ? cqgState.display : submitted === 'rithmic' ? rithmicState.display : null;
  useEffect(() => {
    if (open && submittedDisplay === 'live') store.closeAddAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submittedDisplay]);

  // Concurrent-session answer on a CQG connect → hand off to the legacy dialog, which owns the
  // take-over machinery (TakeOverView / concurrent-login request). Creds/env are already stored,
  // and the dialog re-seeds from storage on open, so nothing is lost in the handoff.
  const cqgConcurrent = cqgState.reason === 'concurrent_session';
  useEffect(() => {
    if (open && submitted === 'cqg' && cqgConcurrent) {
      store.closeAddAccount();
      store.openCqg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitted, cqgConcurrent]);

  const pending = submitted !== null && (
    (submitted === 'cqg' && (cqgConnect.isPending || cqgState.display === 'connecting')) ||
    (submitted === 'rithmic' && (rithmicConnect.isPending || rithmicState.display === 'connecting'))
  );
  const submitError = broker === 'cqg' ? (cqgConnect.isError ? cqgConnect.error : null) : (rithmicConnect.isError ? rithmicConnect.error : null);
  const lastError = submitted === 'cqg' ? cqgState.lastError : submitted === 'rithmic' ? rithmicState.lastError : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(broker);
    try {
      if (broker === 'cqg') {
        await runCqgConnectSequence({
          creds: { username, password, environment },
          mutate: (vars) => cqgConnect.mutateAsync(vars),
        });
      } else {
        await runRithmicConnectSequence({
          // No POP picker on this dialog (2026-07-29): it is the quick add-an-account path, so it
          // reuses whatever location the Rithmic connect dialog last stored — '' = core (Chicago).
          creds: { system, username, password, location: readStoredRithmicCreds().location },
          mutate: () => rithmicConnect.mutateAsync(),
        });
      }
    } catch {
      // POST failure (429 rate-limit, network, 5xx) — surfaced via the mutation's isError below;
      // clear the pending gate so the form is immediately usable again.
      setSubmitted(null);
    }
  };

  const brokerName = broker === 'cqg' ? 'CQG' : 'Rithmic';

  return (
    <ConnectModalShell
      open={open}
      onClose={store.closeAddAccount}
      title={pending ? `Connecting to ${brokerName}` : 'Add account'}
      ariaLabel="Add account"
    >
      {pending ? (
        /* Connection step (2026-07-18): after "Add account" the form is replaced by this progress
           view; the dialog closes into the accounts list only when the session goes LIVE (the
           `submittedDisplay === 'live'` effect above) — i.e. only once the connection is done. A
           concurrent-session answer hands off to the CQG dialog's take-over view instead. */
        <div className="flex flex-col items-center gap-4 py-4">
          <p className="text-center text-[13px] font-semibold leading-5 text-text">
            Connecting to {brokerName}…
          </p>
          <p className="text-center text-[13px] leading-5 text-text-muted">
            Logging in and syncing your account. This can take a moment. It&apos;ll appear in your
            accounts once connected.
          </p>
          <div className="h-1 w-full max-w-[280px] overflow-hidden rounded-full bg-canvas-inset" aria-label="Connecting">
            <div className="h-full w-full animate-pulse rounded-full" style={{ background: BRAND_GRADIENT }} />
          </div>
          {broker === 'rithmic' && <RithmicAttribution className="w-full pt-2" />}
          <div className="flex w-full items-center justify-end">
            <button
              type="button"
              onClick={store.closeAddAccount}
              className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
              style={gradientTextStyle}
            >
              Close
            </button>
          </div>
        </div>
      ) : (
      <form onSubmit={e => void submit(e)} className="flex flex-col gap-6">
        {/* Broker picker (the Figma 316px "Filter Selector"): options FILL the track equally and the
            active pill slides between them (2026-07-18 user direction). */}
        <SegmentedControl
          ariaLabel="Broker"
          value={broker}
          onChange={setBroker}
          fill
          className="w-full max-w-[316px]"
          options={[
            { value: 'cqg', label: <BrokerTabLabel logo="/brokers/cqg.png" name="CQG" /> },
            { value: 'rithmic', label: <BrokerTabLabel logo="/brokers/rithmic.png" name="Rithmic" /> },
          ]}
        />

        <p className="text-[13px] leading-5 text-text-muted">
          External broker accounts used to manage and execute your trades.
        </p>

        {broker === 'cqg' ? (
          <>
            {/* Live = api.cqg.com (real accounts); Demo = demoapi.cqg.com sandbox (demo creds).
                LIVE first + the default since 2026-08-21 (user decision) — matches CqgConnectorDialog. */}
            <SegmentedControl
              ariaLabel="CQG environment"
              size="sm"
              // self-start: don't stretch to the form width (flex-column default) — the track should
              // hug the Demo/Live buttons, not extend past them.
              className="self-start"
              value={environment}
              onChange={setEnvironment}
              options={[
                { value: 'live', label: 'Live' },
                { value: 'demo', label: 'Demo' },
              ]}
            />
            <PillInput icon={<User className="h-5 w-5" />} type="text" value={username} onChange={setUsername} placeholder="Username" autoComplete="username" required />
            <PillInput icon={<KeyRound className="h-5 w-5" />} type="password" value={password} onChange={setPassword} placeholder="Password" autoComplete="current-password" required />
          </>
        ) : (
          <>
            <PillSelect icon={<Server className="h-5 w-5" />} value={system} onChange={setSystem} aria-label="Rithmic system">
              {RITHMIC_SYSTEMS.map(s => <option key={s} value={s}>{s}</option>)}
            </PillSelect>
            <PillInput icon={<User className="h-5 w-5" />} type="text" value={username} onChange={setUsername} placeholder="Username" autoComplete="username" required />
            <PillInput icon={<KeyRound className="h-5 w-5" />} type="password" value={password} onChange={setPassword} placeholder="Password" autoComplete="current-password" required />
          </>
        )}

        {/* The form only renders while NOT pending (the connecting view replaces it on submit), so a
            lingering error here means the last attempt failed — show it above the fields. */}
        {submitError != null && <ErrorMessage error={submitError} />}
        {/* A CQG refusal that links CQG's own page (the EULA) becomes a real button, exactly as in the
            CQG connector dialog (owner ruling 2026-09-23 — prod 2026-09-18 a new trader hit it here as dead
            text and left). Every other message, and the whole Rithmic branch, renders as before. */}
        {lastError && (submitted === 'cqg'
          ? <CqgLogonErrorNotice message={lastError} variant="form" />
          : <div className="text-[13px]" style={{ color: 'var(--loss)' }}>{lastError}</div>)}

        {/* 🔒 Same credential contract as both provider dialogs. */}
        <p className="text-[12px] leading-4 text-text-subtle">
          🔒 Your credentials stay in this browser: they are used only to log in to the broker directly
          and are never sent to or stored on our servers.
        </p>

        {/* Rithmic/OMNE attribution row (conformance requirement — per Rithmic's dialog mock). */}
        {broker === 'rithmic' && <RithmicAttribution />}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={store.closeAddAccount}
            className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
            style={gradientTextStyle}
          >
            Close
          </button>
          <button
            type="submit"
            className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_GRADIENT }}
          >
            Add account
          </button>
        </div>
      </form>
      )}
    </ConnectModalShell>
  );
}

/** Tab label with a small (14px) broker mark before the name — the simple broker icon next to CQG/Rithmic. */
function BrokerTabLabel({ logo, name }: { logo: string; name: string }) {
  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      <img src={logo} alt="" aria-hidden className="h-3.5 w-3.5 object-contain" />
      {name}
    </span>
  );
}
